import React, { useState, useEffect, useRef } from 'react';

// Define the place data interface that will be returned by the component
export interface PlaceData {
  formatted_address: string;
  place_id: string;
  lat: number;
  lng: number;
  name?: string;
  types?: string[];
  fullPlace?: google.maps.places.PlaceResult;
  // Add parsed address components
  address_components?: {
    street_number?: string;
    route?: string;
    locality?: string; // city
    administrative_area_level_1?: string; // state
    postal_code?: string;
    country?: string;
    formatted_street?: string; // combined street_number and route
    place_id?: string; // Google Places ID for the address
  };
}

// Define the component props
export interface AddressAutocompleteProps {
  onSelect: (place: PlaceData) => void;
  placeholder?: string;
  label?: string;
  defaultValue?: string;
  disabled?: boolean;
  required?: boolean;
  searchType?: 'all' | 'establishments' | 'addresses';
  prioritizeBusinesses?: boolean;
}

// Valid place types for address validation
const VALID_PLACE_TYPES = ['street_address', 'premise', 'establishment'];

// Business establishment types that we want to prioritize
const BUSINESS_TYPES = ['establishment', 'point_of_interest', 'store', 'restaurant', 'food'];

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  onSelect,
  placeholder = 'Enter an address',
  label,
  defaultValue = '',
  disabled = false,
  required = false,
  searchType = 'all',
  prioritizeBusinesses = false,
}) => {
  const [inputValue, setInputValue] = useState(defaultValue);
  const [error, setError] = useState<string | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const autocompleteElementRef = useRef<HTMLElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Determine types based on searchType prop
  const getAutocompleteTypes = (): string[] => {
    switch (searchType) {
      case 'establishments':
        return ['establishment'];
      case 'addresses':
        return ['address'];
      case 'all':
      default:
        return ['establishment', 'geocode']; // Include both businesses and addresses
    }
  };

  // Load the Google Maps API script
  useEffect(() => {
    // Check if the script is already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      setIsLoaded(true);
      return;
    }

    // Define the callback function in the global scope
    // This is required for the async loading pattern
    const callbackName = `initGoogleMapsCallback_${Math.random().toString(36).substring(2, 9)}`;
    
    // Extend the window interface to include our dynamic callback
    interface WindowWithCallback extends Window {
      [key: string]: any;
    }
    
    // Create a global callback function that will be called when the API is loaded
    (window as WindowWithCallback)[callbackName] = () => {
      setIsLoaded(true);
      // Clean up the global callback after it's called
      delete (window as WindowWithCallback)[callbackName];
    };

    // Create and load the script
    const script = document.createElement('script');
    // TODO: Improve Google Maps API key handling to ensure it works consistently across environments
    // - Add better error handling for API key issues
    // - Implement a fallback mechanism if the API key is invalid
    // - Consider using a backend proxy approach for better security
    // - Ensure proper domain restrictions are configured in Google Cloud Console
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyDOMydSeKyRsZSRxpl-rd1PsFJOKGtxfew';
    console.log('Using Google Maps API key:', apiKey);
    script.src = `https://maps.googleapis.com/maps/api/js?libraries=places&key=${apiKey}&v=weekly&callback=${callbackName}&loading=async`;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      setError('Failed to load Google Maps API');
      // Clean up the global callback if there's an error
      delete (window as WindowWithCallback)[callbackName];
    };

    document.head.appendChild(script);

    return () => {
      // Clean up the script and callback if the component unmounts before loading completes
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
      delete (window as WindowWithCallback)[callbackName];
    };
  }, []);

  // Initialize the PlaceAutocompleteElement when the API is loaded
  useEffect(() => {
    if (!isLoaded || !containerRef.current) return;

    try {
      // Clear any existing content
      if (containerRef.current.firstChild) {
        containerRef.current.innerHTML = '';
      }

      // Create a custom input element for typing
      const inputElement = document.createElement('input');
      inputElement.type = 'text';
      inputElement.placeholder = placeholder;
      inputElement.disabled = disabled;
      inputElement.value = inputValue;
      inputElement.style.width = '100%';
      inputElement.style.padding = '0.5rem';
      inputElement.style.border = 'none';
      inputElement.style.outline = 'none';
      inputElement.style.backgroundColor = 'transparent';
      inputRef.current = inputElement;
      
      // Create a wrapper div for the input and PlaceAutocompleteElement
      const wrapperDiv = document.createElement('div');
      wrapperDiv.style.position = 'relative';
      wrapperDiv.style.width = '100%';
      
      // Append the input to the wrapper
      wrapperDiv.appendChild(inputElement);
      
      // Append the wrapper to the container
      containerRef.current.appendChild(wrapperDiv);
      
      // Create the PlaceAutocompleteElement
      const placeAutocompleteElement = document.createElement('gmp-placeautocomplete') as HTMLElement;
      placeAutocompleteElement.setAttribute('input', '#hidden-input'); // We'll use our own input
      placeAutocompleteElement.style.position = 'absolute';
      placeAutocompleteElement.style.top = '0';
      placeAutocompleteElement.style.left = '0';
      placeAutocompleteElement.style.width = '100%';
      placeAutocompleteElement.style.height = '100%';
      placeAutocompleteElement.style.opacity = '0'; // Make it invisible
      placeAutocompleteElement.style.pointerEvents = 'none'; // Disable pointer events
      
      // Create a hidden input for the PlaceAutocompleteElement
      const hiddenInput = document.createElement('input');
      hiddenInput.id = 'hidden-input';
      hiddenInput.style.display = 'none';
      wrapperDiv.appendChild(hiddenInput);
      
      // Set component restrictions to US only with enhanced types
      const autocompleteTypes = getAutocompleteTypes();
      const autocompleteOptions = {
        componentRestrictions: { country: 'us' },
        fields: ['address_components', 'formatted_address', 'geometry', 'name', 'place_id', 'types', 'business_status'],
        types: autocompleteTypes
      };
      
      // Set options as a JSON string attribute
      placeAutocompleteElement.setAttribute('options', JSON.stringify(autocompleteOptions));
      
      // Create a place result element to get the selected place
      const placeResultElement = document.createElement('gmp-placeresult') as HTMLElement;
      placeResultElement.style.display = 'none'; // Hide this element as we just need it for data
      wrapperDiv.appendChild(placeResultElement);
      
      // Store the reference
      autocompleteElementRef.current = placeAutocompleteElement;
      
      // Append the PlaceAutocompleteElement to the wrapper
      wrapperDiv.appendChild(placeAutocompleteElement);
      
      // Create a custom autocomplete instance using the Maps JavaScript API
      // Note: We're still using the client-side API for the UI, but we'll use the backend proxy for API requests
      const autocomplete = new google.maps.places.Autocomplete(inputElement, {
        componentRestrictions: { country: 'us' },
        fields: ['address_components', 'formatted_address', 'geometry', 'name', 'place_id', 'types', 'business_status'],
        types: autocompleteTypes
      });
      
      // Add event listener for place selection
      autocomplete.addListener('place_changed', () => {
        try {
          const place = autocomplete.getPlace();
          
          if (!place.geometry) {
            setError('Please select a valid address from the dropdown');
            return;
          }
          
          // Enhanced validation for different search types
          let isValidType = false;
          if (searchType === 'establishments') {
            isValidType = Boolean(place.types?.some((type: string) => BUSINESS_TYPES.includes(type)));
            if (!isValidType) {
              setError('Please select a valid business establishment.');
              return;
            }
          } else {
            isValidType = Boolean(place.types?.some((type: string) => VALID_PLACE_TYPES.includes(type)));
            if (!isValidType) {
              setError('Please select a valid address or business.');
              return;
            }
          }
          
          // Clear any previous errors
          setError(null);
          
          // Parse address components
          const addressComponents: PlaceData['address_components'] = {};
          
          if (place.address_components) {
            for (const component of place.address_components) {
              const types = component.types;
              
              if (types.includes('street_number')) {
                addressComponents.street_number = component.long_name;
              } else if (types.includes('route')) {
                addressComponents.route = component.long_name;
              } else if (types.includes('locality')) {
                addressComponents.locality = component.long_name;
              } else if (types.includes('administrative_area_level_1')) {
                addressComponents.administrative_area_level_1 = component.short_name;
              } else if (types.includes('postal_code')) {
                addressComponents.postal_code = component.long_name;
              } else if (types.includes('country')) {
                addressComponents.country = component.long_name;
              }
            }
          }
          
          // Create formatted street address
          if (addressComponents.street_number && addressComponents.route) {
            addressComponents.formatted_street = `${addressComponents.street_number} ${addressComponents.route}`;
          } else if (addressComponents.route) {
            addressComponents.formatted_street = addressComponents.route;
          }
          
          // Determine display value - prioritize business name if available
          let displayValue = place.formatted_address || '';
          const isBusiness = Boolean(place.types?.some((type: string) => BUSINESS_TYPES.includes(type)));
          
          if (isBusiness && place.name && prioritizeBusinesses) {
            // Format as "Business Name – Address" for establishments
            displayValue = `${place.name} – ${place.formatted_address}`;
          }
          
          // Create the place data object
          const placeData: PlaceData = {
            formatted_address: place.formatted_address || '',
            place_id: place.place_id || '',
            lat: place.geometry.location?.lat() || 0,
            lng: place.geometry.location?.lng() || 0,
            name: place.name,
            types: place.types,
            fullPlace: place,
            address_components: {
              ...addressComponents,
              place_id: place.place_id || '' // Add place_id to address components for easier access
            }
          };
          
          console.log('Selected place data:', placeData);
          console.log('Is business:', isBusiness, 'Business name:', place.name);
          
          // Call the onSelect callback with the place data
          onSelect(placeData);
          
          // Update the input value with enhanced display format
          setInputValue(displayValue);
          
          // Update the hidden input value for PlaceAutocompleteElement
          hiddenInput.value = displayValue;
          
          // Dispatch an input event on the hidden input to update the PlaceAutocompleteElement
          const inputEvent = new Event('input', { bubbles: true });
          hiddenInput.dispatchEvent(inputEvent);
        } catch (error) {
          console.error('Error handling place selection:', error);
          setError('Error processing selected address');
        }
      });
      
      // Sync input changes with the hidden input
      inputElement.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        hiddenInput.value = target.value;
        
        // Dispatch an input event on the hidden input to update the PlaceAutocompleteElement
        const inputEvent = new Event('input', { bubbles: true });
        hiddenInput.dispatchEvent(inputEvent);
      });
      
    } catch (error) {
      console.error('Error initializing Google Places Autocomplete:', error);
      setError('Error initializing address autocomplete');
    }
    
    return () => {
      // Clean up when the component unmounts
      if (autocompleteElementRef.current) {
        // No need to remove event listeners as they're handled by Google Maps API
      }
    };
  }, [isLoaded, placeholder, disabled, onSelect, inputValue, searchType, prioritizeBusinesses]);

  return (
    <div className="address-autocomplete-container" style={{ width: '100%', marginBottom: '1rem' }}>
      {label && (
        <label 
          htmlFor="address-autocomplete-container"
          style={{ 
            display: 'block', 
            marginBottom: '0.5rem', 
            fontWeight: 500,
            color: '#1e1e1e'
          }}
        >
          {label}{required && <span style={{ color: 'red' }}> *</span>}
        </label>
      )}
      
      <div 
        id="address-autocomplete-container"
        ref={containerRef}
        style={{
          width: '100%',
          borderRadius: '0.5rem',
          border: `1px solid ${error ? 'red' : '#ccc'}`,
          boxSizing: 'border-box',
          transition: 'border-color 0.2s ease-in-out',
        }}
        data-testid="address-autocomplete"
      >
        {/* Input and PlaceAutocompleteElement will be inserted here */}
      </div>
      
      {error && (
        <div 
          style={{ 
            color: 'red', 
            fontSize: '0.875rem', 
            marginTop: '0.25rem' 
          }}
        >
          {error}
        </div>
      )}
      
      {!isLoaded && !error && (
        <div 
          style={{ 
            color: '#666', 
            fontSize: '0.875rem', 
            marginTop: '0.25rem' 
          }}
        >
          Loading address autocomplete...
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
